import {
  Check,
  Eye,
  FolderClosed,
  Globe,
  Key,
  Loader2,
  MessageSquareWarning,
  ShieldCheck,
  Terminal,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const CHECKS = [
  { label: "Credential & secret exposure", icon: Key },
  { label: "Destructive shell commands", icon: Terminal },
  { label: "Suspicious network calls", icon: Globe },
  { label: "Unrestricted file access", icon: FolderClosed },
  { label: "Prompt injection patterns", icon: MessageSquareWarning },
  { label: "Hidden / obfuscated instructions", icon: Eye },
] as const;

type State = "queued" | "scanning" | "pass";

export function RiskScanStep({
  publishing,
  onScanComplete,
}: {
  publishing: boolean;
  onScanComplete: () => void;
}) {
  const [states, setStates] = useState<State[]>(() => CHECKS.map(() => "queued"));
  const onComplete = useRef(onScanComplete);
  onComplete.current = onScanComplete;
  const done = useRef(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    CHECKS.forEach((_, i) => {
      timers.push(
        setTimeout(
          () => setStates((p) => p.map((s, idx) => (idx === i ? "scanning" : s))),
          300 + i * 360,
        ),
      );
      timers.push(
        setTimeout(
          () => setStates((p) => p.map((s, idx) => (idx === i ? "pass" : s))),
          300 + i * 360 + 320,
        ),
      );
    });
    timers.push(
      setTimeout(
        () => {
          if (!done.current) {
            done.current = true;
            onComplete.current();
          }
        },
        300 + CHECKS.length * 360 + 500,
      ),
    );
    return () => timers.forEach(clearTimeout);
    // Run the scan animation exactly once on mount; the latest onScanComplete is read via the ref.
  }, []);

  const scanning = states.some((s) => s !== "pass");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "grid size-10 place-items-center rounded-full bg-bg-2 text-fg-link",
            scanning && "animate-spin",
          )}
        >
          {scanning ? <Loader2 className="size-5" /> : <ShieldCheck className="size-5" />}
        </span>
        <div>
          <h3 className="font-display text-lg font-bold text-fg-1">Lightweight risk scan</h3>
          <p className="text-sm text-fg-3">
            Deterministic static checks · no AI inference · runs in seconds
          </p>
        </div>
      </div>

      {CHECKS.map((check, i) => {
        const st = states[i] ?? "queued";
        const Icon = st === "pass" ? Check : st === "scanning" ? Loader2 : check.icon;
        return (
          <div
            key={check.label}
            className={cn(
              "flex items-center gap-3 rounded-[9px] border border-border-1 bg-white px-4 py-3",
              st === "pass" && "border-success/40",
            )}
          >
            <span
              className={cn(
                "grid size-7 place-items-center rounded-full bg-bg-2",
                st === "pass" ? "text-success" : "text-fg-3",
                st === "scanning" && "animate-spin text-fg-link",
              )}
            >
              <Icon className="size-4" />
            </span>
            <span className="flex-1 text-sm text-fg-2">{check.label}</span>
            <span className="text-xs font-semibold text-fg-3">
              {st === "pass" ? "Clear" : st === "scanning" ? "Scanning…" : "Queued"}
            </span>
          </div>
        );
      })}

      {publishing ? <p className="text-center text-sm text-fg-3">Publishing your skill…</p> : null}
    </div>
  );
}
